/* Noxtee behaviours: carousels, countdown, inline video play, count-up numbers. */
(() => {
  if (window.nxReady) return;
  window.nxReady = true;

  const ARROW = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 4l6 6-6 6"/></svg>';

  class NxCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.nx-track');
      if (!this.track || this.dataset.ready) return;
      this.dataset.ready = '1';
      this.slides = Array.from(this.track.children);
      this.dots = this.querySelector('.nx-dots');
      this.prev = this.querySelector('[data-nx-prev]');
      this.next = this.querySelector('[data-nx-next]');
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.sync(), { passive: true });
      new ResizeObserver(() => this.build()).observe(this.track);
      this.build();
    }
    pages() {
      const per = Math.max(1, Math.round(this.track.clientWidth / (this.slides[0]?.getBoundingClientRect().width || 1)));
      return Math.max(1, this.slides.length - per + 1);
    }
    build() {
      if (!this.dots) return this.sync();
      const n = this.pages();
      if (this.dots.children.length !== n) {
        this.dots.innerHTML = '';
        for (let i = 0; i < n; i++) {
          const b = document.createElement('button');
          b.type = 'button'; b.className = 'nx-dot'; b.setAttribute('aria-label', `Go to slide ${i + 1}`);
          b.addEventListener('click', () => this.to(i));
          this.dots.appendChild(b);
        }
      }
      this.closest('.nx-carousel')?.classList.toggle('nx-carousel--static', n <= 1);
      this.sync();
    }
    index() {
      const x = this.track.scrollLeft, w = this.slides[1] ? this.slides[1].offsetLeft - this.slides[0].offsetLeft : 1;
      return Math.round(x / w);
    }
    to(i) {
      const s = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
      if (s) this.track.scrollTo({ left: s.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
    }
    go(d) { this.to(this.index() + d); }
    sync() {
      const i = this.index(), n = this.pages();
      if (this.dots) Array.from(this.dots.children).forEach((d, k) => d.setAttribute('aria-current', String(k === Math.min(i, n - 1))));
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    }
  }
  customElements.define('nx-carousel', NxCarousel);

  class NxCountdown extends HTMLElement {
    connectedCallback() {
      // Counts down a fixed window from page load (same behaviour as the reference store).
      const end = Date.now() + (parseInt(this.dataset.seconds, 10) || 7200) * 1000;
      const out = ['h', 'm', 's'].map((k) => this.querySelector(`[data-${k}]`));
      const tick = () => {
        let t = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (t === 0) clearInterval(this.timer);
        const h = Math.floor(t / 3600); t -= h * 3600;
        const m = Math.floor(t / 60); const s = t - m * 60;
        [h, m, s].forEach((v, k) => { if (out[k]) out[k].textContent = String(v).padStart(2, '0'); });
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('nx-countdown', NxCountdown);

  const resetVideo = (video) => {
    video.pause();
    if (video.readyState > 0) video.currentTime = 0;
    const box = video.closest('.nx-video');
    box?.classList.remove('is-playing');
    const button = box?.querySelector('.nx-video__play');
    if (button) {
      button.setAttribute('aria-label', button.dataset.playLabel);
      button.setAttribute('aria-pressed', 'false');
    }
  };
  const resetVideos = (root = document) => root.querySelectorAll('.nx-video video').forEach(resetVideo);

  document.addEventListener('click', (event) => {
    const box = event.target.closest('.nx-video');
    const video = box?.querySelector('video');
    if (!video) return;
    if (!video.paused && !video.ended) {
      resetVideo(video);
      return;
    }
    resetVideos();
    video.controls = false;
    video.muted = false;
    box.classList.add('is-playing');
    const button = box.querySelector('.nx-video__play');
    button?.setAttribute('aria-label', button.dataset.pauseLabel);
    button?.setAttribute('aria-pressed', 'true');
    video.play().catch(() => resetVideo(video));
  });
  document.addEventListener('ended', (event) => {
    if (event.target.matches('.nx-video video')) resetVideo(event.target);
  }, true);
  document.addEventListener('scroll', (event) => {
    if (event.target instanceof Element && event.target.matches('.nx-track')) resetVideos(event.target);
  }, true);
  if ('IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          const video = entry.target.querySelector('video');
          if (video && !video.paused) resetVideo(video);
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.nx-video').forEach((box) => videoObserver.observe(box));
  }

  const counters = document.querySelectorAll('[data-nx-count]');
  if (counters.length && 'IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      const el = en.target, to = parseInt(el.dataset.nxCount, 10), suffix = el.dataset.suffix || '';
      const t0 = performance.now();
      const step = (t) => { const p = Math.min(1, (t - t0) / 1200); el.textContent = Math.round(to * (1 - Math.pow(1 - p, 3))) + suffix; if (p < 1) requestAnimationFrame(step); };
      requestAnimationFrame(step);
    }), { threshold: 0.4 });
    counters.forEach((c) => { c.textContent = '0' + (c.dataset.suffix || ''); io.observe(c); });
  }
})();
